/**
 * Wertzuordnung eines Entwurfs: jeder unterschiedliche Quelltext je Feld („Erledigt", „Müller",
 * „Peter") mit dem weclapp-Wert, den KIAN gewählt hat — änderbar. Eine Auswahl gilt für ALLE
 * Einträge mit diesem Text; bei einem Import mit 200 Zeilen sind das drei Entscheidungen statt 200.
 */
import { useMemo, useState } from 'react';
import { Badge, Group, Select, Stack, Text, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconHandFinger, IconSparkles } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { KianValueMapEntry, KianValueMapField } from '../../../lib/api/domains/kianActions';
import { VALUE_MAP_FIELD_LABEL } from './kianActionUi';

const NONE = '__none__';

interface Props {
  entries: KianValueMapEntry[];
  editable: boolean;
  onChange: (field: KianValueMapField, key: string, targetId: string | null) => void;
  busy?: boolean;
}

const FIELD_ORDER: KianValueMapField[] = ['customer', 'status', 'assignee', 'priority', 'category'];

export function KianValueMapSection({ entries, editable, onChange, busy }: Props) {
  const grouped = useMemo(() => {
    const m = new Map<KianValueMapField, KianValueMapEntry[]>();
    for (const e of entries) m.set(e.field, [...(m.get(e.field) ?? []), e]);
    return FIELD_ORDER.filter((f) => m.has(f)).map((f) => ({
      field: f,
      entries: [...m.get(f)!].sort((a, b) => Number(!!a.targetId) - Number(!!b.targetId) || b.count - a.count),
    }));
  }, [entries]);

  if (!grouped.length) return null;
  return (
    <Stack gap="xs">
      {grouped.map((g) => (
        <Stack key={g.field} gap={4}>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            {VALUE_MAP_FIELD_LABEL[g.field]}
          </Text>
          {g.entries.map((e) => (
            <ValueRow key={`${e.field}:${e.key}`} entry={e} editable={editable} onChange={onChange} busy={busy} />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

function ValueRow({
  entry,
  editable,
  onChange,
  busy,
}: {
  entry: KianValueMapEntry;
  editable: boolean;
  onChange: Props['onChange'];
  busy?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const customerSearch = useQuery({
    queryKey: ['weclapp-customer-search', debounced],
    queryFn: () => api.searchWeclappCustomers(debounced),
    enabled: entry.field === 'customer' && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const options = useMemo(() => {
    const opts = new Map<string, string>();
    if (entry.targetId) opts.set(entry.targetId, entry.targetName ?? entry.targetId);
    for (const c of entry.candidates ?? []) opts.set(c.id, c.hint ? `${c.label} · ${c.hint}` : c.label);
    for (const c of customerSearch.data?.customers ?? []) {
      opts.set(String(c.id), c.customerNumber ? `${c.name} · Kd.-Nr. ${c.customerNumber}` : c.name);
    }
    return [
      { value: NONE, label: 'Nicht übernehmen (leer lassen)' },
      ...[...opts.entries()].map(([value, label]) => ({ value, label })),
    ];
  }, [entry, customerSearch.data]);

  const missing = !entry.targetId && entry.origin !== 'manual';
  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <Group gap={6} wrap="nowrap" style={{ flex: '0 1 40%', minWidth: 0 }}>
        {missing ? (
          <IconAlertTriangle size={14} color="var(--mantine-color-red-5)" style={{ flexShrink: 0 }} />
        ) : entry.origin === 'manual' ? (
          <Tooltip label="Von dir gewählt">
            <IconHandFinger size={14} style={{ flexShrink: 0, opacity: 0.7 }} />
          </Tooltip>
        ) : (
          <Tooltip label="Von KIAN zugeordnet">
            <IconSparkles size={14} style={{ flexShrink: 0, opacity: 0.7 }} />
          </Tooltip>
        )}
        <Text size="sm" truncate title={entry.source}>
          „{entry.source}"
        </Text>
        {entry.count > 1 ? (
          <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>
            {entry.count}×
          </Badge>
        ) : null}
      </Group>
      <Text size="sm" c="dimmed">
        →
      </Text>
      <Select
        size="xs"
        style={{ flex: 1, minWidth: 0 }}
        data={options}
        value={entry.targetId ?? (entry.origin === 'manual' ? NONE : null)}
        placeholder={missing ? 'Bitte wählen …' : undefined}
        error={missing ? true : undefined}
        disabled={!editable || busy}
        searchable
        onSearchChange={entry.field === 'customer' ? setSearch : undefined}
        nothingFoundMessage={entry.field === 'customer' ? 'Kundennamen eingeben …' : 'Nichts gefunden'}
        onChange={(v) => {
          if (!v) return;
          onChange(entry.field, entry.key, v === NONE ? null : v);
        }}
        comboboxProps={{ zIndex: 5400 }}
      />
    </Group>
  );
}
